/**
 * Background Task Registration — central setup for all background tasks.
 *
 * Called once from DatabaseProvider after db.open() succeeds.
 * Returns the runner instance so it can be stopped on unmount.
 */

import { BackgroundTaskRunner } from "./BackgroundTaskRunner"
import { dayChangeDetection } from "./tasks/dayChangeDetection"
import { orphanedIndexCleanup } from "./tasks/orphanedIndexCleanup"
import { fileGcTask } from "./tasks/fileGcTask"
import { recurrenceFallbackTask } from "./tasks/recurrenceFallback"
import { sharedRealmSyncTask } from "./tasks/sharedRealmSync"
import { closedContainerRepairTask } from "./tasks/closedContainerRepair"

// Migration runner is integrated as a one-shot app-start task
import { runDataMigrations } from "./tasks/dataMigrationRunner"
import type { BackgroundTaskDef } from "./types"

// ---------------------------------------------------------------------------
// Migration wrapper — adapts runDataMigrations() to BackgroundTaskDef
// ---------------------------------------------------------------------------

const dataMigrationTask: BackgroundTaskDef = {
  id: "data-migrations",
  description: "Run pending data migrations (userStatus format, etc.)",
  priority: 3, // Critical — run before day-change and orphan cleanup
  triggers: ["app-start"],
  initialDelayMs: 1000, // 1s — let DB settle

  async run(_signal) {
    const count = await runDataMigrations()
    if (count > 0) {
      console.info(
        `[background] Data migrations complete: ${count} records migrated`,
      )
    }
  },
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Create and start the background task runner with all registered tasks.
 *
 * @returns The runner instance — call `runner.stop()` on app teardown.
 */
export function startBackgroundTasks(): BackgroundTaskRunner {
  const runner = new BackgroundTaskRunner()

  // Register tasks (runner sorts by priority internally)
  runner.register(dataMigrationTask)
  runner.register(dayChangeDetection)
  runner.register(orphanedIndexCleanup)
  runner.register(fileGcTask)
  runner.register(recurrenceFallbackTask)
  runner.register(sharedRealmSyncTask)
  runner.register(closedContainerRepairTask)

  // Start — fires app-start triggers, sets up periodic + visibility
  runner.start()

  return runner
}
